import { useState, useEffect } from 'react';
import * as ReactGA from 'react-ga';
import { Button, Dialog, LabeledContainer, Input, InputError, Radio, Label, Checkbox } from '@schibsted-svp/react-ui';
import config from 'config';
import { Dropzone } from 'components/video/Dropzone/Dropzone';
import { LogoOverlaySelectRHF, hasLogoOverlayOptions } from 'components/video/live/form/LogoOverlaySelect';
import { AccessDefinitionSelectRHFControl } from 'components/video/live/form/AccessDefinitionSelect';
import useNewsroom from 'hooks/useNewsroom';
import { Tab, Tabs } from 'components/ui/Tabs';
import { EncoderStatus } from 'components/video/EncoderStatus';
import { UploadDialogTrigger } from './UploadDialogTrigger';
import { useUploadDialogForm } from './hooks';
import css from './UploadDialog.module.scss';

const { allowedVideoTypes } = config;

export const TABS = {
    LOCAL: 'local',
    URL: 'url',
    WITHOUT_FILE: 'without_file',
} as const;

type Tab = (typeof TABS)[keyof typeof TABS];

const DEFAULT_TAB = TABS.LOCAL;

const FILETYPE = {
    VIDEO: 'video',
    AUDIO: 'audio',
} as const;

type UploadDialogProps = {
    triggerType: 'button' | 'dropdown';
};

export function UploadDialog({ triggerType = 'button' }: UploadDialogProps) {
    const provider = useNewsroom();

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [tab, setTab] = useState<Tab>(DEFAULT_TAB);

    const toggleDialog = () => setIsDialogOpen(!isDialogOpen);

    const { formApi, handleSubmit } = useUploadDialogForm({ provider, toggleDialog });
    const { control, register, reset, setValue, watch, formState } = formApi;
    const { errors, isSubmitting } = formState;
    const drmProtectionEnabled = watch('protections.drmProtection');

    useEffect(() => {
        if (isDialogOpen) {
            reset();
            setTab(DEFAULT_TAB);
            ReactGA.modalview('advanced-filters');
        }
    }, [isDialogOpen, reset]);

    const onTabChange = (newTab: Tab) => {
        reset();
        setTab(newTab);
        setValue('activeTab', newTab);
    };

    const onFileUpload = (files: File[]) => {
        setValue('files', files);
        handleSubmit();
    };

    return (
        <Dialog
            heading="Create asset"
            dialogTrigger={<UploadDialogTrigger triggerType={triggerType} toggleDialog={toggleDialog} />}
            isOpen={isDialogOpen}
            onClose={toggleDialog}
        >
            <form onSubmit={handleSubmit}>
                <Dialog.Section className={css.tabsContainer}>
                    <input hidden {...register('activeTab')} />
                    <Tabs>
                        <Tab label="Local file" isActive={tab === TABS.LOCAL} onClick={() => onTabChange(TABS.LOCAL)} />
                        <Tab label="From URL" isActive={tab === TABS.URL} onClick={() => onTabChange(TABS.URL)} />
                        <Tab
                            label="Without file"
                            isActive={tab === TABS.WITHOUT_FILE}
                            onClick={() => onTabChange(TABS.WITHOUT_FILE)}
                        />
                    </Tabs>
                </Dialog.Section>
                <Dialog.Section>
                    {tab === TABS.LOCAL && (
                        <>
                            <Dropzone
                                accept={allowedVideoTypes}
                                loading={isSubmitting}
                                onUpload={onFileUpload}
                                className={css.dropzone}
                            />
                            <InputError>{errors?.files?.message}</InputError>
                        </>
                    )}
                    {tab === TABS.URL && (
                        <LabeledContainer label="File URL">
                            <Input {...register('fileUrl')} error={errors?.fileUrl?.message} />
                        </LabeledContainer>
                    )}
                    {tab === TABS.WITHOUT_FILE && (
                        <>
                            <Label className={css.fileTypes}>Choose asset type</Label>
                            <Radio {...register('fileType')} defaultValue={FILETYPE.VIDEO} label="Video" />
                            <Radio {...register('fileType')} defaultValue={FILETYPE.AUDIO} label="Audio" />
                        </>
                    )}
                    {hasLogoOverlayOptions(provider) && tab !== TABS.WITHOUT_FILE ? (
                        <LabeledContainer label="Burn-in logo" className={css.burnInLogo}>
                            <LogoOverlaySelectRHF name="overlay" control={control} newsroom={provider} size="compact" />
                        </LabeledContainer>
                    ) : null}
                    {provider === 'vgtv' && config.player.options.drm && tab !== TABS.WITHOUT_FILE && (
                        <LabeledContainer label="Content protection" className={css.drmSection}>
                            <Checkbox {...register('protections.drmProtection')} label="Enable DRM protection" />
                            {drmProtectionEnabled && (
                                <div className={css.drmOptions}>
                                    <Checkbox {...register('protections.geoRestriction')} label="Geoblock" />
                                    <div>
                                        <Label className={css.viewerAccessLabel}>Viewer access</Label>
                                        <AccessDefinitionSelectRHFControl
                                            name="protections.accessLevel"
                                            size="small"
                                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                            control={control as any}
                                        />
                                        <InputError>{errors?.protections?.accessLevel?.message}</InputError>
                                    </div>
                                </div>
                            )}
                        </LabeledContainer>
                    )}
                    <LabeledContainer label="Encoders status" className={css.encodersStatusSection}>
                        <div className={css.encodersStatus}>
                            <EncoderStatus assetType="audio" label="Audio:" />
                            <EncoderStatus assetType="video" label="Video:" />
                        </div>
                    </LabeledContainer>
                </Dialog.Section>
                <Dialog.Section mode="flexRight" variant="darker">
                    <Button type="button" variant="standard" onClick={toggleDialog} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    {(tab === TABS.URL || tab === TABS.WITHOUT_FILE) && (
                        <Button type="submit" variant="primary" disabled={isSubmitting} loading={isSubmitting}>
                            {tab === TABS.URL ? 'Upload' : 'Create'}
                        </Button>
                    )}
                </Dialog.Section>
            </form>
        </Dialog>
    );
}
